import type { Vector3 } from '@kitware/vtk.js/types';
import { vec3, mat4 } from 'gl-matrix';
import type { FrameDataFragment } from '../generated/graphql';

export function generateCoordinateTransforms({
  origin,
  spacing,
  direction,
}: {
  origin: Vector3;
  spacing: Vector3;
  direction: [number, number, number, number, number, number, number, number, number];
}): {
  worldToIndex: (arg1: Vector3) => Vector3;
  indexToWorld: (arg1: Vector3) => Vector3;
} {
  const indexToWorldTransformMatrix = mat4.create();
  const worldToIndexTransformMatrix = mat4.create();

  mat4.fromTranslation(indexToWorldTransformMatrix, [origin[0], origin[1], origin[2]]);

  indexToWorldTransformMatrix[0] = direction[0];
  indexToWorldTransformMatrix[1] = direction[1];
  indexToWorldTransformMatrix[2] = direction[2];
  indexToWorldTransformMatrix[4] = direction[3];
  indexToWorldTransformMatrix[5] = direction[4];
  indexToWorldTransformMatrix[6] = direction[5];
  indexToWorldTransformMatrix[8] = direction[6];
  indexToWorldTransformMatrix[9] = direction[7];
  indexToWorldTransformMatrix[10] = direction[8];

  mat4.scale(indexToWorldTransformMatrix, indexToWorldTransformMatrix, [
    spacing[0],
    spacing[1],
    spacing[2],
  ]);
  mat4.invert(worldToIndexTransformMatrix, indexToWorldTransformMatrix);

  return {
    worldToIndex: (worldCoordinates) => {
      const indexCoordinates = vec3.create();
      vec3.transformMat4(
        // Output - index coordinates
        indexCoordinates,
        // Input - world coordinates
        worldCoordinates,
        // transformation matrix
        worldToIndexTransformMatrix
      );
      return [...indexCoordinates] as Vector3;
    },
    indexToWorld: (indexCoordinates) => {
      const worldCoordinates = vec3.create();
      vec3.transformMat4(
        // Output - world coordinates
        worldCoordinates,
        // Input - index coordinates
        indexCoordinates,
        // indexToWorld transformation matrix (mat4)
        indexToWorldTransformMatrix
      );
      return [...worldCoordinates] as Vector3;
    },
  };
}

export function worldToIndex(
  tags:
    | {
        origin: FrameDataFragment['origin'];
        direction: FrameDataFragment['direction'];
        spacing: FrameDataFragment['spacing'];
      }
    | FrameDataFragment
): (arg1: Vector3) => Vector3 {
  const { origin, direction, spacing } = tags;

  const indexToWorldTransformMatrix = mat4.create();
  const worldToIndexTransformMatrix = mat4.create();

  mat4.fromTranslation(indexToWorldTransformMatrix, [origin[0], origin[1], origin[2]]);

  indexToWorldTransformMatrix[0] = direction.data[0];
  indexToWorldTransformMatrix[1] = direction.data[1];
  indexToWorldTransformMatrix[2] = direction.data[2];
  indexToWorldTransformMatrix[4] = direction.data[3];
  indexToWorldTransformMatrix[5] = direction.data[4];
  indexToWorldTransformMatrix[6] = direction.data[5];
  indexToWorldTransformMatrix[8] = direction.data[6];
  indexToWorldTransformMatrix[9] = direction.data[7];
  indexToWorldTransformMatrix[10] = direction.data[8];

  mat4.scale(indexToWorldTransformMatrix, indexToWorldTransformMatrix, [
    spacing[0],
    spacing[1],
    spacing[2],
  ]);
  mat4.invert(worldToIndexTransformMatrix, indexToWorldTransformMatrix);

  return (worldCoordinates: Vector3) => {
    const indexCoordinates = vec3.create();
    vec3.transformMat4(
      indexCoordinates,
      // world coordinates
      worldCoordinates,
      // worldToIndexTransformMatrix (mat4)
      worldToIndexTransformMatrix
    );
    return [...indexCoordinates] as Vector3;
  };
}

export function indexToWorld(
  tags:
    | {
        origin: FrameDataFragment['origin'];
        direction: FrameDataFragment['direction'];
        spacing: FrameDataFragment['spacing'];
      }
    | FrameDataFragment
): (arg1: Vector3) => Vector3 {
  const { origin, direction, spacing } = tags;

  const indexToWorldTransformMatrix = mat4.create();
  const worldToIndexTransformMatrix = mat4.create();

  mat4.fromTranslation(indexToWorldTransformMatrix, [origin[0], origin[1], origin[2]]);

  indexToWorldTransformMatrix[0] = direction.data[0];
  indexToWorldTransformMatrix[1] = direction.data[1];
  indexToWorldTransformMatrix[2] = direction.data[2];
  indexToWorldTransformMatrix[4] = direction.data[3];
  indexToWorldTransformMatrix[5] = direction.data[4];
  indexToWorldTransformMatrix[6] = direction.data[5];
  indexToWorldTransformMatrix[8] = direction.data[6];
  indexToWorldTransformMatrix[9] = direction.data[7];
  indexToWorldTransformMatrix[10] = direction.data[8];

  mat4.scale(indexToWorldTransformMatrix, indexToWorldTransformMatrix, [
    spacing[0],
    spacing[1],
    spacing[2],
  ]);
  mat4.invert(worldToIndexTransformMatrix, indexToWorldTransformMatrix);

  // @ts-expect-error [EN-7967] - TS2322 - Type '(indexCoordinates: Vector3) => number[]' is not assignable to type '(arg1: Vector3) => Vector3'.
  return (indexCoordinates: Vector3) => {
    const worldCoordinates = vec3.create();
    vec3.transformMat4(
      worldCoordinates,
      // world coordinates
      indexCoordinates,
      // worldToIndexTransformMatrix (mat4)
      indexToWorldTransformMatrix
    );
    return [...worldCoordinates];
  };
}

export const findSmallestTagSmid = (tags: FrameDataFragment[]): string => {
  let smallestIndex = 0;
  for (let i = 1; i < tags.length; i++) {
    if (tags[i].imageNumber < tags[smallestIndex].imageNumber) {
      smallestIndex = i;
    }
  }
  return tags[smallestIndex].smid;
};
